/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { deleteItemByIdCfn } from '../shared.functions';
import { filterPanelItemsCfn } from './functions';
import { Item } from '../item/item.class';
import { PanelItem } from './panel-item.class';
import { Publish } from '../../decorators';

import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { PanelItemDataIn, PanelItemsDataIn, PanelItemsDataOut } from './types';

export class PanelItems extends KitchenDesignBase<PanelItemsDataIn> {
	readonly children: Array<PanelItem>;

	/**
	 * The constructor of the 'PanelItems' class
	 * @param options provides context, also used to hydrate the instance
	 */
	constructor (options: KitchenDesignOptions<PanelItemsDataIn> = {}) {
		super(BlueprintInstanceType.PanelItems, options);

		const { dataIn: data = null } = options;
		this.children = [];

		if (data && data.children && data.children.length > 0) {
			data.children.forEach((n) => this.children.push(new PanelItem({ dataIn: n, parent: this })));
		}

		this.setInitialized();
	}

	/**
	 * removes PanelItem matching the provided id from PanelItem
	 * @param id id of PanelItem to delete
	 * @returns boolean: true if remove, false if error, null when not found
	 */
	// @Publish('panel_item_deleted')
	public delete (id: string): boolean {
		if (deleteItemByIdCfn(this.children, id)) {
			this.refMap.panelItemGroups?.purgeEmpty();
			this.context.cache.delete(id);
			return true;
		}
		return false;
	}

	/**
	 *
	 * @param id id of panel item to find
	 * @returns PanelItem
	 */
	public findById (id: string): PanelItem {
		return this.children.find((pi) => pi.id === id);
	}

	/**
	 * method to return panel items the co-exist with the countertop (if flag passed true) and are not excluded
	 * @returns Array<PanelItem>
	 */
	public filterItems (withCountertopItems = false, includePlacements: Array<string> = []): Array<PanelItem> {
		return filterPanelItemsCfn(this as PanelItems, {
			withCountertopItems,
			includePlacements
		});
	}

	/**
	 * returns the first item matching the include parameter sorted by y
	 * @param include array of placements to include (optional)
	 * @returns Array<PanelItem>
	 */
	public getFirstItem (includePlacements = null): PanelItem {
		return this.filterItems(false, includePlacements)
			.sort((a, b) => a.center.y - b.center.y)[0] as PanelItem;
	}

	/**
	 * Creates and adds a new panelItem
	 *
	 * @param panelItemData serialized data to hydrate new instance with
	 * @returns Instance of PanelItem
	 */
	@Publish('panel_items', 'add', { useReturn: true, useId: true })
	public new (dataIn: PanelItemDataIn = null): PanelItem {
		const panelItem = new PanelItem({ dataIn, parent: this });
		this.children.push(panelItem);

		return panelItem;
	}

	/**
	 * Creates and adds a new panelItem
	 *
	 * @param panelItemData serialized data to hydrate new instance with
	 * @returns Instance of PanelItem
	 */
	@Publish('panel_items', 'add', { useReturn: true, useId: true })
	public newFromSku (sku: string): PanelItem {
		const panelItem = new PanelItem({ dataIn: { item: new Item({ sku }).serialize() }, parent: this });

		// user-placed items are not rule-managed; rule-engine code paths explicitly set ruleManaged = true
		// when they own the item (see add-panel-vents.ts, requires-insulated-jacket.ts).
		// Why: prevents requires-insulated-jacket and delete-panel-vents sweeps from deleting manually-added items.
		panelItem.ruleManaged = false;

		// safety net: snap bottom-only items to panel bottom when constructor snap missed
		if (panelItem.boundBottom && !panelItem.boundTop && panelItem.center.y === 0) {
			const panelItemGroup = panelItem.refMap?.panelItemGroup;
			if (panelItemGroup) {
				const halfPanelHeight = (panelItemGroup.height ?? 0) / 2;
				panelItem.center.y = halfPanelHeight - (panelItem.size.height / 2);
			}
		}

		this.children.push(panelItem);
		return panelItem;
	}

	/**
	 * Creates and adds a new panelItem
	 *
	 * @param panelItemData serialized data to hydrate new instance with
	 * @returns Instance of PanelItem
	 */
	@Publish('panel_items', 'add', { useReturn: true, useId: true })
	public newFromId (id: string): PanelItem {
		const panelItem = new PanelItem({ dataIn: { item: new Item({ id }).serialize() }, parent: this });
		panelItem.ruleManaged = false;
		this.children.push(panelItem);
		return panelItem;
	}

	/**
	 *
	 * @returns serialized data representing class state for persistence
	 */
	public serialize ():PanelItemsDataOut {
		return {
			children: this.children
				.map((panel: PanelItem) => panel.serialize())
				.sort((a, b) => a.center.y - b.center.y)
		};
	}
}
